import { SensorAlert, SensorAlertSeverity, SensorStatus } from "common";
import { useTranslation } from "react-i18next";

import { StatusPillProps } from "../components/StatusPill";

export const useStatusPillProps = () => {
  const { t } = useTranslation();

  const generateAlertStatusPillProps = (
    alert: SensorAlert | null | undefined,
  ): Pick<
    StatusPillProps,
    "outlined" | "error" | "info" | "success" | "warning" | "children"
  > => {
    if (alert?.severity === SensorAlertSeverity.Critical) {
      return {
        children: t("alerts.severity.critical"),
        outlined: false,
        error: true,
        info: false,
        success: false,
        warning: false,
      };
    }

    if (alert?.severity === SensorAlertSeverity.High) {
      return {
        children: t("alerts.severity.high"),
        outlined: false,
        error: true,
        info: false,
        success: false,
        warning: false,
      };
    }

    if (alert?.severity === SensorAlertSeverity.Medium) {
      return {
        children: t("alerts.severity.medium"),
        outlined: false,
        error: false,
        info: false,
        success: false,
        warning: true,
      };
    }

    if (alert?.severity === SensorAlertSeverity.Low) {
      return {
        children: t("alerts.severity.low"),
        outlined: false,
        error: false,
        info: false,
        success: false,
        warning: true,
      };
    }

    return {
      children: t("alerts.severity.info"),
      outlined: false,
      error: false,
      info: true,
      success: false,
      warning: false,
    };
  };

  const generateSensorStatusPillProps = (
    status: SensorStatus | undefined,
    alert?: SensorAlert,
  ): Pick<
    StatusPillProps,
    "outlined" | "error" | "success" | "warning" | "children"
  > => {
    if (
      alert?.severity === SensorAlertSeverity.Critical ||
      alert?.severity === SensorAlertSeverity.High
    ) {
      return {
        children: t("sensors.statusPill.criticalAlert"),
        outlined: false,
        error: true,
        success: false,
        warning: false,
      };
    }

    if (
      alert?.severity === SensorAlertSeverity.Medium ||
      alert?.severity === SensorAlertSeverity.Low
    ) {
      return {
        children: t("sensors.statusPill.mediumAlert"),
        outlined: false,
        error: false,
        success: false,
        warning: true,
      };
    }

    if (
      alert?.severity === SensorAlertSeverity.Medium ||
      alert?.severity === SensorAlertSeverity.Low
    ) {
      return {
        children: t("sensors.statusPill.lowAlert"),
        outlined: false,
        error: false,
        success: false,
        warning: false,
      };
    }

    if (status === SensorStatus.Active) {
      return {
        children: t("sensors.statusPill.active"),
        outlined: true,
        error: false,
        success: true,
        warning: false,
      };
    }

    if (status === SensorStatus.Maintenance) {
      return {
        children: t("sensors.statusPill.maintenance"),
        outlined: true,
        error: false,
        success: false,
        warning: true,
      };
    }

    if (status === SensorStatus.Pending) {
      return {
        children: t("sensors.statusPill.pending"),
        outlined: true,
        error: false,
        success: false,
        warning: true,
      };
    }

    return {
      children: t("sensors.statusPill.inactive"),
      outlined: true,
      error: false,
      success: false,
      warning: false,
    };
  };

  return {
    generateAlertStatusPillProps,
    generateSensorStatusPillProps,
  };
};
